import { z } from 'zod';

/**
 * Registry of every feature flag the platform evaluates through OpenFeature.
 *
 * The zod enum is the single source of truth for flag keys: compile-time
 * safety comes from the inferred `FeatureFlagKey` union, runtime safety from
 * parsing untrusted input (e.g. tRPC procedure input) with the schema.
 *
 * Keys must match the flag key configured in every provider backend (Datadog
 * Feature Flags, the local flagd definition file in `api-stack/flagd/`, and
 * any in-memory test configuration).
 */
export const featureFlagKeySchema = z.enum([
  'easter_eggs',
  'object_toolbar',
  'indicator_results_chart_enhancements',
  'indicator_widget_tolerances',
]);

export type FeatureFlagKey = z.infer<typeof featureFlagKeySchema>;

export interface FeatureFlagDefinition {
  /**
   * Value used when the provider is unreachable, the flag is missing from the
   * backend, or evaluation fails. Defaults are deliberately the "feature off"
   * value so a flagging outage never enables unfinished behaviour.
   */
  defaultValue: boolean;
  description: string;
}

export const featureFlagDefinitions: Record<
  FeatureFlagKey,
  FeatureFlagDefinition
> = {
  easter_eggs: {
    defaultValue: false,
    description:
      'Gates hidden easter-egg UI — currently the drag "bounciness" slider on the customisable ribbon filter editor.',
  },
  object_toolbar: {
    defaultValue: false,
    description:
      'Gates the object-level toolbar (CUS-27) that relocates per-object actions (export/delete) plus Save/Cancel into a single header bar. Piloted on the Indicator detail page (CUS-135) before fanning out to the other registers.',
  },
  indicator_results_chart_enhancements: {
    defaultValue: false,
    description:
      'Gates Deliverable 1 of the indicator chart enhancements — the results-tab Actions dropdown, date range picker, chart x-axis/data changes, and PNG export.',
  },
  indicator_widget_tolerances: {
    defaultValue: false,
    description:
      'Gates Deliverable 2 of the indicator chart enhancements — the tolerances/appetites toggle on the dashboard line chart widget.',
  },
};

export const featureFlagKeys: FeatureFlagKey[] = featureFlagKeySchema.options;
